import convex from '../convexClient';
import { api } from '../../convex/_generated/api';

const MAX_PDF_PAGES = 10;
const MAX_PDF_CHARS = 16000;

let pdfModulePromise = null;

const loadPdfModule = async () => {
    if (!pdfModulePromise) {
        pdfModulePromise = import('pdfjs-dist/legacy/build/pdf.mjs').then(async (pdfjs) => {
            const worker = await import('pdfjs-dist/legacy/build/pdf.worker.mjs?url');
            pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
            return pdfjs;
        });
    }
    return pdfModulePromise;
};

export const uploadFileToChat = async (chatId, file) => {
    const uploadUrl = await convex.mutation(api.ai.generateAttachmentUploadUrl, { chatId });
    const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
            'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
    });

    if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
    }

    const payload = await response.json();
    if (!payload.storageId) {
        throw new Error('Upload did not return a storageId.');
    }

    return payload.storageId;
};

export const extractPdfText = async (file) => {
    const pdfjs = await loadPdfModule();
    const buffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: buffer }).promise;
    const pages = Math.min(pdf.numPages, MAX_PDF_PAGES);
    const chunks = [];

    for (let pageNumber = 1; pageNumber <= pages; pageNumber += 1) {
        const page = await pdf.getPage(pageNumber);
        const textContent = await page.getTextContent({ disableStream: true });
        const pageText = textContent.items
            .map((item) => ('str' in item ? item.str : ''))
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();
        if (pageText) {
            chunks.push(pageText);
        }
        const joined = chunks.join('\n\n');
        if (joined.length >= MAX_PDF_CHARS) {
            return joined.slice(0, MAX_PDF_CHARS);
        }
    }

    return chunks.join('\n\n').slice(0, MAX_PDF_CHARS);
};

export const createPendingAttachment = async (file) => {
    const kind = file.type === 'application/pdf' ? 'pdf' : 'image';
    const url = URL.createObjectURL(file);
    const extractedText = kind === 'pdf' ? await extractPdfText(file) : undefined;

    return {
        id: `${file.name}:${file.size}:${file.lastModified}`,
        file,
        kind,
        name: file.name,
        contentType: file.type || (kind === 'pdf' ? 'application/pdf' : 'image/png'),
        size: file.size,
        extractedText,
        url,
    };
};

export const finalizePendingAttachment = async (chatId, attachment) => {
    const storageId = await uploadFileToChat(chatId, attachment.file);
    return {
        kind: attachment.kind,
        name: attachment.name,
        contentType: attachment.contentType,
        size: attachment.size,
        extractedText: attachment.extractedText,
        url: attachment.url,
        storageId,
    };
};

export const revokeAttachmentPreview = (attachment) => {
    if (attachment?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(attachment.url);
    }
};
